import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class ProductPrice extends Component {
  #closestSection;
  #priceMap = new Map();
  #observer;
  #syncFrame;
  #currentVariantId = '';

  connectedCallback() {
    super.connectedCallback();
    this.#closestSection = this.closest('.shopify-section, dialog');
    if (!this.#closestSection) return;

    this.#priceMap = this.#readPriceMap();
    this.#currentVariantId = this.#getCurrentVariantId() || '';

    this.#closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#closestSection.addEventListener('change', this.#handleInteraction, true);
    this.#closestSection.addEventListener('input', this.#handleInteraction, true);
    this.#closestSection.addEventListener('click', this.#handleInteraction, true);

    this.#observer = new MutationObserver(() => this.#queueSync());
    this.#observer.observe(this.#closestSection, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['checked', 'selected', 'value', 'aria-checked', 'aria-selected'],
    });

    this.#queueSync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.#closestSection) return;
    this.#closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#closestSection.removeEventListener('change', this.#handleInteraction, true);
    this.#closestSection.removeEventListener('input', this.#handleInteraction, true);
    this.#closestSection.removeEventListener('click', this.#handleInteraction, true);
    this.#observer?.disconnect();
    if (this.#syncFrame) cancelAnimationFrame(this.#syncFrame);
  }

  #readPriceMap() {
    if (!this.#closestSection) return new Map();
    const scripts = this.#closestSection.querySelectorAll('script[data-sm-variant-price-map]');
    const script = Array.from(scripts).find((node) => node.dataset.productId === this.dataset.productId);
    if (!script?.textContent) return new Map();

    try {
      const rows = JSON.parse(script.textContent);
      return new Map(rows.map((row) => [String(row.id), row]));
    } catch (error) {
      console.warn('[product-price] Price map unavailable.', error);
      return new Map();
    }
  }

  #normalizeId(value) {
    if (!value) return '';
    return String(value).trim().split('/').pop();
  }

  #getPriceContainer() {
    return this.querySelector('[ref="priceContainer"]');
  }

  #getCurrentVariantId() {
    if (!this.#closestSection) return '';

    const forms = Array.from(this.#closestSection.querySelectorAll('product-form-component'));
    const productForm = forms.find((form) => form.dataset.productId === this.dataset.productId);
    const variantInput = productForm?.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement && variantInput.value) return variantInput.value;

    const pickers = Array.from(this.#closestSection.querySelectorAll('variant-picker'));
    const picker = pickers.find((node) => node.dataset.productId === this.dataset.productId);
    const selected = picker?.querySelector('select option:checked, fieldset input:checked');
    return selected?.dataset?.variantId || '';
  }

  #getSelectedSellingPlanId() {
    if (!this.#closestSection) return '';

    const controls = Array.from(
      this.#closestSection.querySelectorAll(
        'input[name="selling_plan"], input[name="selling_plan_id"], select[name="selling_plan"], select[name="selling_plan_id"]'
      )
    );

    const active = controls.find((control) => {
      if (control instanceof HTMLSelectElement) return Boolean(control.value);
      if (!(control instanceof HTMLInputElement)) return false;
      if (control.type === 'radio' || control.type === 'checkbox') return control.checked && Boolean(control.value);
      return Boolean(control.value);
    });

    if (!(active instanceof HTMLInputElement || active instanceof HTMLSelectElement)) return '';
    return this.#normalizeId(active.value);
  }

  #findSellingPlan(variantId, sellingPlanId) {
    const row = this.#priceMap.get(String(variantId));
    if (!row || !Array.isArray(row.sellingPlans)) return null;
    return row.sellingPlans.find((plan) => this.#normalizeId(plan.id) === this.#normalizeId(sellingPlanId)) || null;
  }

  #handleInteraction = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const picker = event.target.closest('variant-picker');
    if (picker?.dataset.productId === this.dataset.productId) {
      const selected = event.target instanceof HTMLSelectElement ? event.target.selectedOptions[0] : event.target;
      const variantId = selected?.dataset?.variantId;
      if (variantId) this.#currentVariantId = String(variantId);
      this.#syncPrice();
      this.#queueSync();
      return;
    }

    const purchaseControl = event.target.closest(
      '[name="selling_plan"], [name="selling_plan_id"], [id*="selling-plan" i], [class*="selling-plan" i], [id*="subscription" i], [class*="subscription" i], [id*="subscribe" i], [class*="subscribe" i], [id^="shopify-block-"]'
    );
    if (!purchaseControl) return;

    this.#queueSync();
    setTimeout(() => this.#queueSync(), 30);
    setTimeout(() => this.#queueSync(), 120);
  };

  #queueSync() {
    if (!this.isConnected || !this.#closestSection) return;
    if (this.#syncFrame) cancelAnimationFrame(this.#syncFrame);
    this.#syncFrame = requestAnimationFrame(() => {
      this.#syncFrame = undefined;
      this.#syncPrice();
    });
  }

  #syncPrice() {
    const variantId = this.#currentVariantId || this.#getCurrentVariantId();
    if (!variantId) return;
    this.#currentVariantId = String(variantId);

    const sellingPlanId = this.#getSelectedSellingPlanId();
    if (sellingPlanId) {
      const plan = this.#findSellingPlan(variantId, sellingPlanId);
      if (plan) {
        this.#renderPrice(plan.price, '', false, true);
        return;
      }
    }

    const row = this.#priceMap.get(String(variantId));
    if (!row) return;
    this.#renderPrice(row.price, row.compareAtPrice, Boolean(row.onSale), false);
  }

  #renderPrice(price, compareAtPrice, onSale, subscriptionActive) {
    const priceContainer = this.#getPriceContainer();
    if (!priceContainer || !price) return;

    priceContainer.querySelectorAll('.price').forEach((element) => this.#replaceVisibleText(element, price));
    priceContainer.querySelectorAll('.compare-at-price').forEach((element) => {
      if (onSale && compareAtPrice && !subscriptionActive) {
        element.removeAttribute('hidden');
        this.#replaceVisibleText(element, compareAtPrice);
      } else {
        element.setAttribute('hidden', '');
      }
    });

    if (subscriptionActive) {
      priceContainer.dataset.subscriptionPriceActive = 'true';
    } else {
      delete priceContainer.dataset.subscriptionPriceActive;
      delete priceContainer.dataset.sellingPlanId;
    }
  }

  #replaceVisibleText(element, value) {
    const hidden = element.querySelector('.visually-hidden');
    if (!hidden) {
      element.textContent = value;
      return;
    }

    for (const node of Array.from(element.childNodes)) {
      if (node !== hidden) node.remove();
    }
    element.append(document.createTextNode(value));
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        const currentPrice = this.#getPriceContainer();
        const newPrice = newProductPrice?.querySelector('[ref="priceContainer"]');
        if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);

        const currentNote = this.querySelector('[ref="volumePricingNote"]');
        const newNote = newProductPrice?.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          currentNote?.remove();
        } else if (!currentNote) {
          this.#getPriceContainer()?.insertAdjacentElement('afterend', newNote.cloneNode(true));
        } else {
          currentNote.replaceWith(newNote);
        }

        const installmentsInput = this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`);
        if (installmentsInput instanceof HTMLInputElement) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.#priceMap = this.#readPriceMap();
        this.#currentVariantId = String(detail.resource?.id || this.#getCurrentVariantId() || this.#currentVariantId);
        this.#queueSync();
        setTimeout(() => this.#queueSync(), 60);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Product update failed.', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
